import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Stack,
  styled,
  useTheme,
  Typography,
} from '@mui/material';
import { ExpandCircleDown } from '@mui/icons-material';
import { Chip } from '@shared/components';
import { useAppTFunction } from '@retail/app/i18n';
import { useMemo } from 'react';
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import { dataGridWithCellBordersClass } from '@shared/styles';

export interface ImportStateValidationProps {
  invalidDataRows?: {
    index: number;
    errors: {
      column?: string;
      reason: string;
      value?: unknown;
    }[];
  }[];
  numberOfInvalidRowsLabel?: string;
  noRows?: boolean;
  missingRequiredColumns?: string[];
}

interface ErrorRow {
  index: number;
  column?: string;
  reason: string;
  value?: unknown;
}

export function ImportStateValidation({
  invalidDataRows,
  numberOfInvalidRowsLabel,
  noRows,
  missingRequiredColumns,
}: ImportStateValidationProps) {
  const t = useAppTFunction();
  const theme = useTheme();
  const palette = theme.vars.palette;

  const errorRows: ErrorRow[] | undefined = useMemo(
    () =>
      invalidDataRows?.flatMap(
        ({ index, errors }) =>
          errors.map((error) => ({
            ...error,
            index,
          })) ?? [],
        [invalidDataRows]
      ),
    [invalidDataRows]
  );

  const rowErrorColumns: GridColDef<ErrorRow>[] = useMemo(
    () => [
      {
        field: 'index',
        flex: 1,
        headerName: t('campaign.import.error.table.index'),
      },
      {
        field: 'column',
        flex: 1,
        headerName: t('campaign.import.error.table.column'),
        rowSpanValueGetter: () => null,
      },
      {
        field: 'reason',
        flex: 1,
        headerName: t('campaign.import.error.table.reason'),
        rowSpanValueGetter: () => null,
      },
      {
        field: 'value',
        flex: 1,
        headerName: t('campaign.import.error.table.value'),
        rowSpanValueGetter: () => null,
      },
    ],
    [t]
  );

  const invalidDateRowsCount = invalidDataRows?.length;

  return (
    <Stack gap={1}>
      {!!missingRequiredColumns?.length && (
        <Alert severity="error">
          <Stack gap={1}>
            <StyledAccordion>
              <AccordionSummary expandIcon={<ExpandCircleDown />}>
                <Typography sx={{ pr: 2 }} variant="subtitle1">
                  {t('common.import.validation.error.missingRequiredColumns')}
                </Typography>
              </AccordionSummary>

              <AccordionDetails>
                {missingRequiredColumns.map((column) => (
                  <Chip
                    backgroundColor={palette.background.paper}
                    textColor={palette.text.primary}
                    label={column}
                    key={column}
                  />
                ))}
              </AccordionDetails>
            </StyledAccordion>
          </Stack>
        </Alert>
      )}
      {!!invalidDateRowsCount && (
        <Alert
          severity="error"
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Stack gap={1}>
            <Typography variant="subtitle1">
              {numberOfInvalidRowsLabel ??
                t('common.import.validation.error.itemRows', {
                  count: invalidDateRowsCount,
                })}
            </Typography>

            <StyledAccordion>
              <AccordionSummary expandIcon={<ExpandCircleDown />} id="mgItem">
                {invalidDateRowsCount === 1
                  ? t('markup.messages.rows.singular')
                  : t('markup.messages.rows.plural')}
              </AccordionSummary>

              <AccordionDetails sx={{ maxWidth: '700px' }}>
                <DataGridPremium<ErrorRow>
                  columns={rowErrorColumns}
                  rows={errorRows}
                  rowSpanning
                  getRowId={(row) => `${row.index}${row.column}`}
                  hideFooter
                  className={dataGridWithCellBordersClass}
                />
              </AccordionDetails>
            </StyledAccordion>
          </Stack>
        </Alert>
      )}
      {noRows && (
        <Alert
          severity="info"
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography variant="subtitle1">{t('markup.messages.emptyFile')}</Typography>
        </Alert>
      )}
    </Stack>
  );
}

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  backgroundColor: 'inherit',
  border: `1px solid ${theme.vars.palette.error.main}`,
}));
